import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { BAND_UI } from "@/lib/bands";
import { FrogFace } from "@/components/FrogArt";
// eslint-disable-next-line no-unused-vars
import { TipButton } from "@/components/TipButton"; // hidden for now — used in header when re-enabled
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, Download, Fish, Zap, Gift, Rocket, TrendingUp, Waves } from "lucide-react";

const Stat = ({ label, value, sub, icon: Icon, testId, accent }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
    <div className={`flex items-center gap-2 ${accent || "text-slate-400"}`}>
      <Icon className="w-4 h-4" />
      <p className="text-xs font-bold uppercase tracking-widest">{label}</p>
    </div>
    <p className="font-display text-3xl font-bold text-slate-800 mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const TadpoleTrend = ({ logs, idx }) => {
  const days = logs.filter((l) => l.tadpoles?.length);
  if (!days.length) return null;
  const totalCleared = days.reduce((s, d) => s + d.tadpoles.filter((t) => t.done).length, 0);
  const totalAll = days.reduce((s, d) => s + d.tadpoles.length, 0);
  const maxCount = Math.max(...days.map((d) => d.tadpoles.length));
  return (
    <div data-testid={`tadpole-trend-${idx}`} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-400">
          <Fish className="w-4 h-4" />
          <p className="text-xs font-bold uppercase tracking-widest">Tadpoles cleared per day</p>
        </div>
        <p className="text-xs font-bold text-sky-600">{totalCleared}/{totalAll} total</p>
      </div>
      <div className="flex items-end gap-2 h-24">
        {days.map((d) => {
          const cleared = d.tadpoles.filter((t) => t.done).length;
          return (
            <div key={d.id} className="flex flex-col items-center gap-1 flex-1 max-w-[48px]" title={`Day ${d.day_number}: ${cleared}/${d.tadpoles.length} tadpoles`}>
              <p className="text-[10px] font-bold text-slate-500">{cleared}/{d.tadpoles.length}</p>
              <div className="w-full bg-sky-100 rounded-md relative flex flex-col justify-end" style={{ height: `${(d.tadpoles.length / maxCount) * 64}px`, minHeight: "10px" }}>
                <div className="w-full bg-sky-500 rounded-md" style={{ height: `${d.tadpoles.length ? (cleared / d.tadpoles.length) * 100 : 0}%` }} />
              </div>
              <p className="text-[10px] text-slate-400 font-semibold">D{d.day_number}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DayStrip = ({ logs }) => (
  <div className="flex gap-1.5 flex-wrap">
    {Array.from({ length: 14 }, (_, i) => {
      const log = logs.find((l) => l.day_number === i + 1);
      const cls =
        log?.status === "done"
          ? log.was_hard
            ? "bg-orange-400"
            : "bg-green-500"
          : log?.status === "reset"
            ? "bg-sky-300"
            : log
              ? "bg-yellow-300"
              : "bg-slate-100";
      return (
        <div key={i} title={log ? `Day ${i + 1}: ${log.title} (${log.status})` : `Day ${i + 1}`} className={`w-7 h-7 rounded-md ${cls} flex items-center justify-center text-[10px] font-bold ${log ? "text-white" : "text-slate-400"}`}>
          {i + 1}
        </div>
      );
    })}
  </div>
);

export default function ParentDashboard() {
  const nav = useNavigate();
  const code = localStorage.getItem("frogfirst_code");
  const [kids, setKids] = useState([]);
  const [bands, setBands] = useState(null);

  const load = useCallback(async () => {
    try {
      const hh = await api.getHousehold(code);
      const full = await Promise.all(hh.children.map((c) => api.getChild(c.id)));
      setKids(full);
    } catch {
      nav("/");
    }
  }, [code, nav]);

  useEffect(() => {
    if (!code) return nav("/");
    load();
    api.getBands().then(setBands);
  }, [code, nav, load]);

  const rewardMoment = (k) => {
    const d = k.stats.done_count;
    if (d >= 14) return "Program complete — plan the big celebration you promised!";
    if (d >= 10) return "10+ frogs eaten. A meaningful reward moment is due.";
    if (d >= 7) return "Halfway there! A small treat or privilege lands well now.";
    if (d >= 3) return "3 frogs in — a specific verbal praise moment works best here.";
    return "First frogs are the hardest. Celebrate the very first one warmly.";
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button data-testid="parent-back-btn" variant="ghost" onClick={() => nav("/home")} className="rounded-lg">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="font-display text-xl font-bold text-slate-800">
              <span className="bg-gradient-to-br from-sky-500 to-blue-500 bg-clip-text text-transparent">Aqua</span> Tadpole · Parent Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Tip button hidden for now — re-enable by adding: <TipButton householdCode={code} /> */}
            <FrogFace size={36} />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-12">
        {kids.length === 0 && (
          <p className="text-slate-500">No kids yet — add one from the home screen.</p>
        )}

        {kids.map((k, idx) => (
          <motion.section
            key={k.id}
            data-testid={`parent-kid-section-${k.name.toLowerCase().replace(/\s/g, "-")}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="space-y-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-white" style={{ backgroundColor: k.color }}>
                {k.name[0].toUpperCase()}
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-800">{k.name}</h2>
                <p className="text-xs text-slate-500">
                  {BAND_UI[k.age_band]?.label} • {k.cycle > 1 ? `Pond ${k.cycle} • ` : ""}Day {Math.min(k.stats.days_logged, 14)} of 14
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat testId={`stat-completion-${idx}`} label="Completion" value={`${k.stats.completion_rate}%`} sub={`${k.stats.done_count} of 14 frogs eaten`} icon={TrendingUp} />
              <Stat testId={`stat-started-first-${idx}`} label="Started with frog" value={`${k.stats.started_first_rate}%`} sub="of completed days" icon={Rocket} />
              <Stat testId={`stat-hard-days-${idx}`} label="Hard days" value={k.stats.hard_days.length} sub={k.stats.hard_days.length ? `Days ${k.stats.hard_days.join(", ")}` : "None yet"} icon={Zap} accent="text-sky-600" />
              <Stat testId={`stat-easy-days-${idx}`} label="Easy days used" value={k.stats.reset_count} sub="Streak bent, not broken" icon={Waves} />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">14-day map{k.cycle > 1 ? ` — pond ${k.cycle}` : ""}</p>
              <DayStrip logs={k.logs} />
              {k.past_cycles?.map((pc) => (
                <div key={pc.cycle} className="pt-2">
                  <p className="text-[11px] font-bold text-slate-400 mb-1">Pond {pc.cycle} ({pc.stats.done_count}/14 frogs)</p>
                  <DayStrip logs={pc.logs} />
                </div>
              ))}
              <p className="text-[11px] text-slate-400">green = done • orange = done but hard • blue = easy day • yellow = frog picked, not finished</p>
            </div>

            <TadpoleTrend logs={k.logs} idx={idx} />

            <div className="bg-amber-50 rounded-xl border border-amber-200 p-5 flex items-start gap-3">
              <Gift className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Reward moment (you decide the reward)</p>
                <p data-testid={`reward-suggestion-${idx}`} className="text-sm text-slate-700 mt-1">{rewardMoment(k)}</p>
              </div>
            </div>
          </motion.section>
        ))}

        {bands && (
          <section className="space-y-5">
            <h2 className="font-display text-2xl font-bold text-slate-800">Printable Packs & Parent Scripts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(bands).map(([key, b]) => (
                <div key={key} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
                  <p className="font-display font-bold text-slate-800">{b.label}</p>
                  <p className="text-xs text-slate-500 flex-1">{b.title} — frog cards, wall chart, scripts & substitute list</p>
                  <a href={api.pdfUrl(key)} download>
                    <Button data-testid={`download-pdf-${key}`} variant="outline" className="w-full rounded-lg border-2 font-semibold">
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </a>
                </div>
              ))}
            </div>

            <Accordion type="single" collapsible className="bg-white rounded-xl border border-slate-200 shadow-sm px-5">
              {Object.entries(bands).map(([key, b]) => (
                <AccordionItem key={key} value={key} className="border-slate-100">
                  <AccordionTrigger data-testid={`script-accordion-${key}`} className="font-display font-semibold text-slate-800 hover:no-underline">
                    Parent script — {b.label}: {b.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="space-y-3 pb-2">
                      {b.parent_script.map((s, i) => (
                        <li key={i} className="flex gap-3 text-sm text-slate-600">
                          <span className="w-6 h-6 rounded-full bg-lime-100 text-lime-700 font-bold flex items-center justify-center shrink-0 text-xs">{i + 1}</span>
                          {s}
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}
      </main>
    </div>
  );
}
